import { useEffect, useState } from "react";
import { getAnchor } from "@/lib/launcher-anchors";

/** Space reserved under/above the peek: the same 7rem the open panel keeps. */
const RESERVE_PX = 112;
/** A peek whose top sits above this many px is cut off by the frame. */
const CLIPPED_TOP_PX = 8;
const SETTLE_MS = 250;

/**
 * Height cap for the launcher peek INSIDE the iframe embed.
 *
 * `100dvh` cannot be used there: the iframe viewport is the closed frame, whose height is
 * measured from the peek itself, so a dvh cap makes the frame ramp up one step per resize
 * round trip (a visibly growing, clipped bubble). The peek is therefore uncapped and the
 * frame grows to fit it. Only when the HOST page clamps the frame (the loader caps it to the
 * host viewport) does the peek end up cut off at the top; then, once the frame has settled,
 * it is capped to what the clamped frame can show. The cap is released when the frame size
 * changes (host window resized) and re-evaluated.
 *
 * Returns the cap in px, or `null` for "no cap".
 */
export function useIframePeekCap(enabled: boolean, peekId: string | undefined): number | null {
  const [cap, setCap] = useState<{ px: number; viewport: number } | null>(null);

  useEffect(() => {
    setCap(null);
    if (!enabled || !peekId || typeof window === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current: { px: number; viewport: number } | null = null;
    const evaluate = () => {
      const viewport = window.innerHeight;
      if (current) {
        if (current.viewport === viewport) return;
        current = null;
        setCap(null);
        schedule();
        return;
      }
      const el = getAnchor("peek");
      if (!el) return;
      if (el.getBoundingClientRect().top < CLIPPED_TOP_PX) {
        current = { px: Math.max(0, viewport - RESERVE_PX), viewport };
        setCap(current);
      }
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(evaluate, SETTLE_MS);
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    const el = getAnchor("peek");
    if (el) ro?.observe(el);
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      clearTimeout(timer);
      ro?.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [enabled, peekId]);

  return cap?.px ?? null;
}
